import DtoolsReviewLineCard from "@/components/sales/dtools/DtoolsReviewLineCard";
import DtoolsOptionComponentGroupsSection from "@/components/sales/dtools/DtoolsOptionComponentGroupsSection";
import DtoolsOptionEquipmentView from "@/components/sales/dtools/DtoolsOptionEquipmentView";
import DtoolsOptionHighlightsPanel from "@/components/sales/dtools/DtoolsOptionHighlightsPanel";
import DtoolsOptionRoomBreakdownSection from "@/components/sales/dtools/DtoolsOptionRoomBreakdownSection";
import DtoolsPdfCompactLineTable from "@/components/sales/dtools/DtoolsPdfCompactLineTable";
import DtoolsPdfSystemValueBar from "@/components/sales/dtools/DtoolsPdfSystemValueBar";
import DtoolsReviewOverviewPanel from "@/components/sales/dtools/DtoolsReviewOverviewPanel";
import DtoolsWhyThisOptionPanel from "@/components/sales/dtools/DtoolsWhyThisOptionPanel";
import DtoolsOptionSectionActions, {
  type DtoolsOptionSectionActionsProps,
} from "@/components/sales/dtools/DtoolsOptionSectionActions";
import { useDtoolsPdfPackageMode } from "@/components/sales/dtools/DtoolsPdfPackageContext";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { resolvePdfSystemValueStory } from "@/lib/proposalPdfSystemStory";
import { PROPOSAL_ACCENT, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { PROPOSAL_RETAIL_PRICE_DISCLAIMER } from "@shared/proposalRetailPriceDisclaimer";
import {
  buildDtoolsReviewSystemView,
  collectServiceLines,
  resolveSystemDisplayLabel,
  type DtoolsReviewBuildContext,
} from "@shared/dtoolsReviewNavigation";
import type { DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import { buildWhyThisOptionText } from "@shared/dtoolsWhyThisOption";
import {
  scaleProposalCustomerPricingForSystemTotal,
  type ProposalCustomerPricingDisplay,
} from "@shared/proposalCustomerPricing";

type Props = {
  system: DtoolsReviewSystem;
  reviewContext?: DtoolsReviewBuildContext;
  optionIndex?: number;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  sectionId?: string;
  /** Hide room breakdown (legacy pricing table). */
  showRoomBreakdown?: boolean;
  /** Show Sort by Option / Sort by Room equipment toggle. */
  showEquipmentSort?: boolean;
  /** Override section heading (v2 independent sections use system name). */
  sectionTitle?: string;
  /** Footer total label — defaults to "Option total". */
  totalLabel?: string;
  /** Hide the section total bar at the bottom of the component list. */
  hideSectionTotal?: boolean;
  /** Customer JSON proposal system tab: no top summary, retail total + footer CTAs at bottom. */
  customerSystemPage?: boolean;
  showRetailPriceDisclaimer?: boolean;
  /** Negotiated pricing display for the inline proposal summary block. */
  customerPricing?: ProposalCustomerPricingDisplay;
};

export default function DtoolsReviewSystemSection({
  system,
  reviewContext,
  optionIndex = 0,
  optionActions,
  sectionId,
  showRoomBreakdown = true,
  showEquipmentSort = false,
  sectionTitle,
  totalLabel = "Option total",
  hideSectionTotal = false,
  customerSystemPage = false,
  showRetailPriceDisclaimer = false,
  customerPricing,
}: Props) {
  const pdfPackageMode = useDtoolsPdfPackageMode();
  const view = buildDtoolsReviewSystemView(system, reviewContext, optionIndex);
  const displayName = sectionTitle ?? resolveSystemDisplayLabel(system, reviewContext, optionIndex);
  const approvalPricing =
    customerPricing != null
      ? scaleProposalCustomerPricingForSystemTotal(customerPricing, view.overview.systemTotal)
      : optionActions?.customerPricing;

  const serviceLines = [
    ...system.rooms.flatMap((room) => collectServiceLines(room.primaries)),
    ...collectServiceLines(system.systemLevelPrimaries),
  ];

  return (
    <section
      id={sectionId}
      className={`${pdfPackageMode ? "space-y-4" : "space-y-6"} ${pdfPackageMode ? "" : "pdf-keep-together "}min-w-0 scroll-mt-24`}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      {!pdfPackageMode ? (
        <div className="border-b border-[#C8A96A]/40 pb-2">
          <h2 className="text-lg font-semibold tracking-tight sm:text-xl" style={{ color: PROPOSAL_TEXT }}>
            {displayName}
          </h2>
        </div>
      ) : null}

      {pdfPackageMode ? (
        <DtoolsPdfSystemValueBar
          amountFormatted={formatDtoolsMoney(view.overview.systemTotal)}
          story={resolvePdfSystemValueStory(system)}
        />
      ) : null}

      <DtoolsReviewOverviewPanel view={view} />
      <DtoolsOptionHighlightsPanel highlights={view.overview.highlights} />
      <DtoolsWhyThisOptionPanel body={buildWhyThisOptionText(system, displayName)} />
      {showEquipmentSort && !pdfPackageMode ? (
        <DtoolsOptionEquipmentView system={system} />
      ) : (
        <DtoolsOptionComponentGroupsSection system={system} />
      )}

      {serviceLines.length > 0 ? (
        pdfPackageMode ? (
          <DtoolsPdfCompactLineTable
            title="Services"
            lines={serviceLines}
            itemHeader="Service"
            testId="dtools-pdf-services"
          />
        ) : (
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em]" style={{ color: PROPOSAL_TEXT }}>
              Services
            </h3>
            <div className="space-y-2">
              {serviceLines.map((line) => (
                <DtoolsReviewLineCard key={line.id} line={line} compact />
              ))}
            </div>
          </section>
        )
      ) : null}

      {showRoomBreakdown ? <DtoolsOptionRoomBreakdownSection view={view} /> : null}

      {!hideSectionTotal && !customerSystemPage ? (
        <div
          className="flex items-baseline justify-between gap-4 rounded-xl border border-[#C8A96A]/30 bg-[#FAFAF7]/80 px-5 py-4"
          style={{ fontFamily: PROPOSAL_COPY_FONT }}
        >
          <p className="text-sm font-semibold uppercase tracking-[0.12em]" style={{ color: PROPOSAL_TEXT }}>
            {totalLabel}
          </p>
          <p className="text-lg font-semibold tabular-nums" style={{ color: PROPOSAL_ACCENT }}>
            {formatDtoolsMoney(view.overview.systemTotal)}
          </p>
        </div>
      ) : null}

      {customerSystemPage ? (
        <div
          className={
            pdfPackageMode
              ? "pdf-keep-together space-y-1 rounded-lg border border-[#C8A96A]/60 bg-[#FAFAF7] px-4 py-2.5"
              : "space-y-2 border-t border-[#1A2744]/10 pt-6"
          }
          data-testid="customer-system-page-total"
        >
          {pdfPackageMode ? (
            <div className="flex items-baseline justify-between gap-4">
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#C8A96A]">
                Total Investment (SGD)
              </p>
              <p className="text-lg font-semibold tabular-nums text-[#1A2744]">
                {formatDtoolsMoney(view.overview.systemTotal)}
              </p>
            </div>
          ) : (
            <p className="text-xl font-semibold tabular-nums text-[#1A2744]">
              TOTAL: {formatDtoolsMoney(view.overview.systemTotal)}
            </p>
          )}
          {showRetailPriceDisclaimer ? (
            <p className={pdfPackageMode ? "text-[10px] leading-snug text-[#4B5563]" : "text-sm leading-relaxed text-[#1A2744]/65"}>
              {PROPOSAL_RETAIL_PRICE_DISCLAIMER}
            </p>
          ) : pdfPackageMode ? (
            <p className="text-[10px] leading-snug text-[#4B5563]">
              All prices are in Singapore Dollars (SGD) and inclusive of prevailing taxes.
            </p>
          ) : null}
        </div>
      ) : null}

      {optionActions && !pdfPackageMode ? (
        <DtoolsOptionSectionActions
          optionLabel={displayName}
          {...optionActions}
          customerPricing={approvalPricing ?? optionActions.customerPricing}
          variant={customerSystemPage ? "system-page-footer" : "full"}
        />
      ) : null}
    </section>
  );
}
